// FPLeagueDetail.js
// Franchise Player — a single league: overview, captain shop, weekly lineups
// and standings.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { doc, onSnapshot, getDoc, collection, query, where } from "firebase/firestore";
import { useAuth } from "./App";
import {
  FP_ACCENT, FP_ACCENT_DIM, POSITION_COLORS, STATUS_LABELS,
  leagueStatus, formatDeadline, timeUntil, entryLabel,
} from "./fp-theme";
import { CAPTAIN_PRICING, formatMoney } from "./fp-config";
import FPCaptainShop from "./FPCaptainShop";
import FPLineupBuilder from "./FPLineupBuilder";
import FPStandings from "./FPStandings";

function Stat({ label, value, sub }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, letterSpacing: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

export default function FPLeagueDetail({ onMount, onLeave }) {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [league, setLeague] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [entries, setEntries] = useState([]);
  const [memberNames, setMemberNames] = useState({});
  const [tab, setTab] = useState("overview");
  const [copied, setCopied] = useState(false);
  const [, setTick] = useState(0);

  // Live league doc
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "fp_leagues", leagueId),
      (snap) => {
        setLeague(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoaded(true);
      },
      (e) => { console.error("League load failed:", e); setLoaded(true); }
    );
    return unsub;
  }, [leagueId]);

  // Live entries for the whole league
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "fp_entries"), where("leagueId", "==", leagueId)),
      (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Entries load failed:", e)
    );
    return unsub;
  }, [leagueId]);

  // Re-render every 30s so countdowns and open/closed status stay current
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Header subtitle
  useEffect(() => {
    if (league?.name) onMount?.(league.name);
    return () => onLeave?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league?.name]);

  // Display names for every member
  const membersKey = (league?.members || []).join(",");
  useEffect(() => {
    if (!league?.members?.length) return;
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        league.members.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, "users", uid));
            return [uid, snap.exists() ? snap.data().displayName || "Manager" : "Manager"];
          } catch {
            return [uid, "Manager"];
          }
        })
      );
      if (!cancelled) setMemberNames(Object.fromEntries(pairs));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersKey]);

  // Open the shop tab by default while it's open and you haven't bought yet
  const myEntries = entries.filter((e) => e.ownerUid === user.uid).sort((a, b) => a.entryNumber - b.entryNumber);
  const statusKey = league ? leagueStatus(league) : null;
  useEffect(() => {
    if (statusKey === "shop_open" && myEntries.length === 0 && tab === "overview") setTab("shop");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey, myEntries.length]);

  if (!loaded) {
    return <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading league...</p>;
  }

  if (!league || !league.members?.includes(user.uid)) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
        <p style={{ color: "rgba(255,255,255,0.5)", marginBottom: 16 }}>
          {league ? "You're not a member of this league." : "League not found."}
        </p>
        <button className="btn btn-fp btn-sm" onClick={() => navigate("/franchise-player")}>← My Leagues</button>
      </div>
    );
  }

  if (statusKey === "legacy") {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1, marginBottom: 6 }}>{league.name}</div>
        <p style={{ color: "rgba(255,255,255,0.5)", marginBottom: 16, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
          This test league was created under the old captain-draft format, which has been replaced by the captain shop. Create a new league to play.
        </p>
        <button className="btn btn-fp btn-sm" onClick={() => navigate("/franchise-player")}>← My Leagues</button>
      </div>
    );
  }

  const shopOpen = statusKey === "shop_open";
  const status = STATUS_LABELS[statusKey];
  const isCommissioner = league.commissionerUid === user.uid;
  const inviteLink = `${window.location.origin}/franchise-player?join=${league.joinCode}`;
  const canShareCode = league.openInvite || isCommissioner;

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  const entriesByUid = entries.reduce((acc, e) => {
    (acc[e.ownerUid] = acc[e.ownerUid] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="animate-fade-in">
      {/* League header */}
      <div style={{ marginBottom: 20 }}>
        <button onClick={() => navigate("/franchise-player")} className="btn btn-ghost btn-sm" style={{ marginBottom: 10 }}>← My Leagues</button>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 34, letterSpacing: 2, lineHeight: 1 }}>
          {league.name}
          {league.test && (
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 800, letterSpacing: 1, marginLeft: 10, padding: "2px 8px", borderRadius: 4, color: "#FBBF24", border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.1)", verticalAlign: "middle" }}>
              TEST
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span style={{ color: status.color, fontWeight: 700 }}>
            {status.label}{shopOpen ? ` · closes in ${timeUntil(league.shopDeadline)}` : ` · since week ${league.startWeek}`}
          </span>
          <span>· {league.members.length} {league.members.length === 1 ? "manager" : "managers"}</span>
          <span>· Commissioner: {league.commissionerName || memberNames[league.commissionerUid] || "—"}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="nav-tabs">
        {[
          ["overview", "Overview"],
          ["shop", shopOpen ? "Captain Shop" : "My Franchise Player"],
          ["lineup", "My Lineup"],
          ["standings", "Standings"],
        ].map(([id, label]) => (
          <button
            key={id}
            className={"nav-tab" + (tab === id ? " active" : "")}
            onClick={() => setTab(id)}
            style={tab === id ? { background: FP_ACCENT, color: "white" } : {}}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {league.message && (
            <div className="card" style={{ borderLeft: `4px solid ${FP_ACCENT}`, padding: "14px 18px" }}>
              <div style={sectionLabel}>FROM THE COMMISSIONER</div>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{league.message}</p>
            </div>
          )}

          {/* Shop status */}
          {shopOpen && (
            <div className="card" style={{ padding: "16px 18px", borderColor: "rgba(20,184,166,0.35)", background: `linear-gradient(135deg, ${FP_ACCENT_DIM}, var(--navy-card))` }}>
              <div style={sectionLabel}>CAPTAIN SHOP OPEN</div>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.5, marginBottom: 12 }}>
                Buy your franchise player before <b>{formatDeadline(league.shopDeadline)}</b>, the first kickoff of week {league.startWeek}.
                {" "}{myEntries.length === 0 ? "You haven't created your entry yet." : `You have ${myEntries.length} ${myEntries.length === 1 ? "entry" : "entries"} set.`}
              </p>
              <button className="btn btn-fp btn-sm" onClick={() => setTab("shop")}>🎖️ Go to the Captain Shop</button>
            </div>
          )}

          {/* Invite */}
          {shopOpen && canShareCode && (
            <div className="card" style={{ padding: "16px 18px" }}>
              <div style={sectionLabel}>INVITE MANAGERS</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, letterSpacing: 6, color: FP_ACCENT }}>{league.joinCode}</div>
                <button onClick={copyInvite} className="btn btn-fp btn-sm">{copied ? "✓ Link copied" : "🔗 Copy invite link"}</button>
              </div>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 8 }}>
                Anyone with the code can join until the captain shop closes.
              </p>
            </div>
          )}

          {/* League numbers */}
          <div className="card" style={{ padding: "16px 18px" }}>
            <div style={sectionLabel}>LEAGUE RULES</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
              <Stat label="CAPTAIN BUDGET" value={formatMoney(league.captainBudget)} sub={`Unspent dollars add to your weekly budget, up to ${formatMoney(league.maxKickback)}`} />
              <Stat label="WEEKLY BUDGET" value={formatMoney(league.weeklyBudget)} sub="For the other 8 lineup spots (incl. a D/ST) around your franchise player" />
              <Stat label="FRANCHISE PLAYER" value={`${league.captainMultiplier}x`} sub="Points, every week, all season. No replacements." />
              <Stat label="BENCH CREDIT" value={formatMoney(league.benchCredit)} sub={`Bench him any week for this. ${formatMoney(league.irBenchCredit)} if he's Out or on IR.`} />
              <Stat label="ENTRIES" value={`Up to ${league.maxEntries}`} sub="Per person, each with its own franchise player" />
              <Stat label="PRICES" value="FanDuel" sub={`Captains: ${CAPTAIN_PRICING[league.pricing]?.label || league.pricing}. Half-PPR scoring.`} />
            </div>
          </div>

          {/* Managers + entries */}
          <div className="card" style={{ padding: "16px 18px" }}>
            <div style={{ ...sectionLabel, display: "flex", justifyContent: "space-between" }}>
              <span>MANAGERS</span>
              <span style={{ color: "rgba(255,255,255,0.3)" }}>{entries.length} {entries.length === 1 ? "entry" : "entries"}</span>
            </div>
            {shopOpen && (
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 10 }}>
                Everyone's franchise players are revealed when the shop closes.
              </p>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {league.members.map((uid) => {
                const theirs = (entriesByUid[uid] || []).sort((a, b) => a.entryNumber - b.entryNumber);
                const reveal = !shopOpen || uid === user.uid;
                return (
                  <div key={uid} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
                    background: uid === user.uid ? FP_ACCENT_DIM : "rgba(255,255,255,0.03)",
                    borderRadius: 8, padding: "10px 12px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{memberNames[uid] || "Manager"}</span>
                      {uid === league.commissionerUid && <span className="badge badge-gray">Commish</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {theirs.length === 0 && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>{shopOpen ? "Hasn't bought yet" : "No entry"}</span>}
                      {theirs.map((e) => reveal ? (
                        <span key={e.id} className="badge" style={{ background: FP_ACCENT_DIM, color: "white", border: "1px solid rgba(20,184,166,0.35)" }}>
                          <b style={{ marginRight: 6 }}>{entryLabel(e)}</b>
                          <span style={{ color: POSITION_COLORS[e.captain.pos], marginRight: 4 }}>{e.captain.pos}</span>
                          {e.captain.name}
                        </span>
                      ) : (
                        <span key={e.id} className="badge badge-gray">🔒 {entryLabel(e)}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Captain shop ── */}
      {tab === "shop" && <FPCaptainShop league={league} myEntries={myEntries} allEntries={entries} />}

      {/* ── Weekly lineup ── */}
      {tab === "lineup" && <FPLineupBuilder league={league} myEntries={myEntries} />}

      {/* ── Standings ── */}
      {tab === "standings" && <FPStandings league={league} user={user} />}
    </div>
  );
}

const sectionLabel = {
  fontSize: 11, fontWeight: 700, letterSpacing: 2,
  color: "rgba(255,255,255,0.4)", marginBottom: 10,
};
